import { Set } from 'immutable';
import { call, put, select } from 'redux-saga/effects';

import { CurrentStakeType } from '../types';
import { MainHubSubmitStakeError } from './staking/types';
import type { SagaIterator } from '@redux-saga/types';

import { StakeType } from 'app/lib/staking/constants';

import { CheckAvailabilitySuccessPayload } from '../actions';
import {
  ResolveActions,
  resolveByAcceptAvailability,
  resolveByCancelInsert,
  resolveByReplaceEntries,
  setupConflict,
} from '../actions/conflict';
import {
  cancelInsert,
  insertFailedEntryInBetSlip, InsertFailedEntryInBetSlipPayload,
  insertInBetSlip, InsertInQueueForAdd,
} from '../actions/entries';
import { ConflictCase } from '../constants';
import { selectBets, selectInsertsQueue, selectMatches } from '../selectors/entries';
import { selectCurrentStakeType } from '../selectors/stakeTypes';
import { EntriesBets, EntriesMatches } from '../types/entries';
import checkAvailability, { isCauseInError } from './checkAvailability';
import { getFailurePayloadByErrorCode } from './staking/errors';

// ---

export default function* addEntryFlow(payload: InsertInQueueForAdd['payload']): SagaIterator {
  const betId = payload.bet.entity.id;

  const insertsQueue = yield select(selectInsertsQueue);

  if (!insertsQueue.has(betId)) {
    console.error(`addEntryFlow -> the insertion of #${betId} is not queued`);
    return;
  }

  const bets: EntriesBets = yield select(selectBets);

  if (bets.has(betId)) {
    console.error(`addEntryFlow -> the bet slip entry #${betId} is already in`);
    return;
  }

  // -- requesting stake types availability -- //

  let availabilityResult: CheckAvailabilitySuccessPayload;

  const nextEntries = bets.set(betId, payload.bet); // adding the new bet entry

  try {
    availabilityResult = yield call(checkAvailability, nextEntries);
  }
  catch (errorResponse) {
    if (!errorResponse || !isCauseInError(errorResponse)) {
      console.error('addEntryFlow -> check availability request failed');
      yield put(cancelInsert(betId)); // cancel entry insertion,
      return; // ... then exit the "add entry" flow,
      // ... preventing conflicts detection, entry insertion and match calculation flow.
    }

    // Может случиться, если пользователь выбрал исход, далее закрылся прием на матч
    // И он выбирает другой исход из матча, с бэка придет ошибка, а мы в свою очередь
    // Добавляем ставку в корзину и отображаем ошибку
    const errorPayload: InsertFailedEntryInBetSlipPayload = { ...payload };
    const failureResponse = { betErrors: isCauseInError(errorResponse) && errorResponse.cause };
    const {
      error,
      matchErrors,
      betErrors,
    } = getFailurePayloadByErrorCode(failureResponse as MainHubSubmitStakeError);

    if (matchErrors) {
      errorPayload.matchErrors = matchErrors;
    }
    if (betErrors) {
      errorPayload.betErrors = betErrors;
    }

    return yield put(insertFailedEntryInBetSlip(error, errorPayload));
  }

  const {
    singles,
    express,
    systems,
    availableStakeTypes,
  } = availabilityResult;

  const insertEntryPayload = {
    ...payload,
    singles,
    express,
    systems,
    availableStakeTypes,
  };

  // -- detecting insertion conflicts -- //
  let resolution = null;
  const stakeType: CurrentStakeType = yield select(selectCurrentStakeType);

  // "single stake type" is active and became unavailable
  if (stakeType.has(StakeType.SINGLE) && !availableStakeTypes.has(StakeType.SINGLE)) {
    resolution = {
      case: ConflictCase.SINGLE,
      entries: [payload],
      actions: ([
        resolveByCancelInsert(betId),
      ]) as ResolveActions[],
    };
  }
  else {
    const hasExpressConflict = stakeType.has(StakeType.EXPRESS) && (
      !availableStakeTypes.has(StakeType.EXPRESS)
    );

    const hasSystemConflict = stakeType.has(StakeType.SYSTEM) && (
      !availableStakeTypes.has(StakeType.SYSTEM)
    );

    if (hasExpressConflict || hasSystemConflict) {
      let conflictCase: ConflictCase | null = null;
      let highlightEntryIds = Set<number>();

      const matchId = payload.match.entity.id;
      const matches: EntriesMatches = yield select(selectMatches);

      const matchById = matches.get(matchId);

      if (matchById) {
        highlightEntryIds = matchById.betIds.toSet();
      }

      if (hasExpressConflict) {
        conflictCase = ConflictCase.EXPRESS;
      }
      else if (hasSystemConflict) {
        conflictCase = ConflictCase.SYSTEM;
      }

      resolution = {
        case: conflictCase,
        entries: [payload],
        highlightEntryIds,
        actions: ([
          highlightEntryIds.size && resolveByReplaceEntries(payload),
          resolveByCancelInsert(betId),
          availableStakeTypes.has(StakeType.SINGLE) && resolveByAcceptAvailability(
            insertEntryPayload,
          ),
        ].filter(Boolean)) as ResolveActions[],
      };
    }
  }

  if (resolution !== null) {
    yield put(setupConflict(resolution));
    return; // exit the "add entry" flow,
    // ... preventing entry insertion and match calculation flow.
  }

  // no conflicts or resolved w/o cancelling

  yield put(insertInBetSlip(insertEntryPayload));
}
